import React, {Dispatch, SetStateAction, useEffect, useRef, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    VStack,
    Stack,
    HStack,
    StackProps,
    Text,
    TextProps,
    useMultiStyleConfig,
    AccordionProps,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
} from '@chakra-ui/react'
import {Accordion, Button, GridLayoutControls, Icon, IconButton} from '_uiComponents'
import {RefinementsType} from '_types'
import {useViewport} from '_customHooks'
import {RemoveScroll} from 'react-remove-scroll'
import {ListingFiltersItem} from './partials/listingFiltersItem'

interface FiltersListProps {
    filtering: {
        isOpened: boolean
        filters?: RefinementsType
        excludedFilters: string[]
        selectedFilters: {[key: string]: string | string[]}
        filterTotals?: number
        toggleFilter: (filter: {value: string}, attributeId: string, selected: boolean, allowMultiple: boolean, action?: string) => void
        setIsFilterListOpened: Dispatch<SetStateAction<boolean>>
        handleReset: () => void
        onConfirmFilters?: () => void
        resetFiltersDisabled?: boolean
    }
    layout: {
        listingLayout: 'grid3' | 'grid2'
        setListingLayout: (layout: 'grid3' | 'grid2') => void
    }
    sorting: {
        sortingOptions: Array<{id: string; label: string; value?: string}>
        selectedSortingOptionLabel: {id: string; label: string; value?: string}
        limit: number
        handleSorting: (sortValue: string) => void
    }
}

/**
 * @name FiltersList
 *
 * @component
 *
 * @description
 * This component renders a list of filters that can be applied to refine search results.
 * It supports both desktop and mobile views, with additional functionality for mobile devices.
 *
 * @param {FiltersListProps} props - The props for the FiltersList component.
 * @param {boolean} props.isOpened - Indicates whether the filter list is currently opened.
 * @param {Array} props.filters - The list of available filters to display.
 * @param {Array<string>} [props.excludedFilters] - A list of filter attribute IDs to exclude from the display.
 * @param {Record<string, Array<string>>} props.selectedFilters - The currently selected filters, grouped by attribute ID.
 * @param {number} props.filterTotals - The total number of results after applying filters.
 * @param {Function} props.toggleFilter - A function to toggle the selection of a filter value.
 * @param {Function} props.setIsFilterListOpened - A function to toggle the visibility of the filter list.
 * @param {Function} props.handleReset - A function to reset all active filters.
 *
 * @returns {JSX.Element} The rendered FiltersList component.
 *
 * @example
 * ```tsx
 * <FiltersList
 *   isOpened={true}
 *   filters={[{ attributeId: 'color', label: 'Color', values: [{ value: 'red', label: 'Red', hitCount: 10 }] }]}
 *   excludedFilters={['size']}
 *   selectedFilters={"c_microcategoryacme": "Cardigan"}
 *   filterTotals={100}
 *   toggleFilter={(filterValue, attributeId, isChecked) => console.log(filterValue, attributeId, isChecked)}
 *   setIsFilterListOpened={(isOpened) => console.log(isOpened)}
 *   handleReset={() => console.log('Reset filters')}
 * />
 * ```
 */
const FiltersList = React.memo(({filtering, layout, sorting}: FiltersListProps) => {
    const {
        isOpened,
        filters: _filters,
        excludedFilters,
        selectedFilters,
        filterTotals,
        toggleFilter,
        setIsFilterListOpened,
        handleReset,
        onConfirmFilters,
        resetFiltersDisabled,
    } = filtering
    const {listingLayout, setListingLayout} = layout
    const {sortingOptions, selectedSortingOptionLabel, handleSorting} = sorting

    const styles = useMultiStyleConfig('FiltersList') as Record<string, StackProps & TextProps & AccordionProps>
    const {isMobile} = useViewport()
    const {formatMessage} = useIntl()
    const [isOpen, setIsOpen] = useState(isOpened)

    const closeBtnRef = useRef<HTMLDivElement | null>(null)
    const prevFocusedElement = useRef<HTMLElement | null>(null)
    const drawerRef = useRef<HTMLDivElement>(null)
    const drawerContentRef = useRef<HTMLDivElement>(null)

    const drawerTitleId = 'listingFilters__drawer-title'
    const sortByLabel = formatMessage({id: 'label.plp.filters.sorting', defaultMessage: 'Sort by'})
    const gridLayoutLabel = formatMessage({id: 'label.plp.filters.viewOption', defaultMessage: 'View option'})
    const filters = excludedFilters ? _filters?.filter(({attributeId}) => !excludedFilters?.includes(attributeId)) : _filters

    const closeDrawer = () => {
        setIsOpen(false)
        setIsFilterListOpened(false)
    }

    useEffect(() => {
        setIsOpen(isOpened)
        // Store the previously focused element
        prevFocusedElement.current = document.activeElement as HTMLElement
        // Focus the close button when Toast opens
        if (closeBtnRef.current && isOpened) {
            closeBtnRef.current?.focus()
        }

        if (isOpened) {
            setTimeout(() => {
                if (drawerContentRef.current) {
                    drawerContentRef.current.setAttribute('aria-labelledby', drawerTitleId)
                }
            }, 500)
        }

        return () => {
            // Restore focus to the previously focused element on close
            prevFocusedElement.current?.focus()
        }
    }, [isOpened])

    return (
        <Drawer isOpen={isOpen} onClose={closeDrawer} size={'lg'} variant={'listingFilters'} placement={'right'}>
            <DrawerOverlay />
            <DrawerContent ref={drawerContentRef}>
                <DrawerHeader>
                    <HStack {...styles.headerWrapper}>
                        <Text id={drawerTitleId} {...styles.headerTitle}>
                            <Text {...styles.headerTitle} as="span" data-testid="plp-filters-title">
                                <FormattedMessage id="label.plp.filters" defaultMessage="FILTERS" />
                            </Text>
                        </Text>
                    </HStack>

                    <Box {...styles.closeIcon}>
                        <IconButton
                            size="iconButtonSm"
                            icon={<Icon name={'IconClose'} size={'md'} />}
                            onClick={closeDrawer}
                            aria-label={formatMessage({id: 'alt.link.close', defaultMessage: 'Close'})}
                        />
                    </Box>
                </DrawerHeader>

                <DrawerBody ref={drawerRef}>
                    <Stack as={RemoveScroll} enabled={isOpened && isMobile} data-testid="filters-list">
                        {filters && filters?.length > 0 && isOpened && (
                            <Accordion allowMultiple={true} reduceMotion={true} {...styles.accordionWrapper}>
                                {/* sort by */}
                                <ListingFiltersItem
                                    filter={{
                                        attributeId: 'sortby',
                                        label: sortByLabel,
                                        values: sortingOptions.map((o) => ({
                                            value: o.id,
                                            label: o.label,
                                        })),
                                    }}
                                    selectedFilters={[selectedSortingOptionLabel?.id || 'best-matches']}
                                    filterType={'radio'}
                                    title={sortByLabel}
                                    ariaLabel={sortByLabel}
                                    handleSorting={handleSorting}
                                    screenReaderCategoryLabelToExpand={formatMessage(
                                        {id: 'label.open.filter.refinements', defaultMessage: 'Show filter {0}'},
                                        {0: sortByLabel},
                                    )}
                                    screenReaderCategoryLabelExpanded={formatMessage(
                                        {id: 'label.close.filter.refinements', defaultMessage: 'Hide filter {0}'},
                                        {0: sortByLabel},
                                    )}
                                />

                                {/* grid layout */}
                                <ListingFiltersItem title={gridLayoutLabel} ariaLabel={gridLayoutLabel} expandable={false} previewSelectedValue={false}>
                                    <GridLayoutControls listingLayout={listingLayout} setListingLayout={setListingLayout} />
                                </ListingFiltersItem>

                                {filters?.map((filter) => {
                                    let selectedFiltersArray = selectedFilters?.[filter.attributeId] ?? []

                                    if (Array.isArray(selectedFiltersArray)) {
                                        selectedFiltersArray = selectedFiltersArray.map((value) =>
                                            typeof value === 'string' ? value.replace(/&/g, '%26') : value,
                                        )
                                    } else if (typeof selectedFiltersArray === 'string') {
                                        selectedFiltersArray = selectedFiltersArray.replace(/&/g, '%26')
                                    }

                                    if (!Array.isArray(selectedFiltersArray)) {
                                        selectedFiltersArray = [selectedFiltersArray]
                                    }
                                    // TODO: DY Search Refinement Issue 
                                    /** Ordina numericamente le taglie quando possibile
                                     * e alfabeticamente per tutti gli altri valori */
                                    const sortedFilter =
                                        filter?.values && (filter.attributeId === 'c_filter-size' || filter.attributeId === 'size')
                                            ? {
                                                  ...filter,
                                                  values: [...filter.values].sort((a, b) => {
                                                      const aValue = String(a.label ?? a.value ?? '')
                                                      const bValue = String(b.label ?? b.value ?? '')
                                                      const aNum = Number(aValue)
                                                      const bNum = Number(bValue)
                                                      const aIsNumber = !Number.isNaN(aNum)
                                                      const bIsNumber = !Number.isNaN(bNum)
                                                      if (aIsNumber && bIsNumber) {
                                                          return aNum - bNum
                                                      }
                                                      // fallback alfabetico naturale
                                                      return aValue.localeCompare(bValue, undefined, {
                                                          numeric: true,
                                                          sensitivity: 'base',
                                                      })
                                                  }),
                                              }
                                            : filter

                                    const screenReaderCategoryLabelToExpand = formatMessage(
                                        {id: 'label.open.filter.refinements', defaultMessage: 'Show filter {0}'},
                                        {0: filter.label},
                                    )
                                    const screenReaderCategoryLabelExpanded = formatMessage(
                                        {id: 'label.close.filter.refinements', defaultMessage: 'Hide filter {0}'},
                                        {0: filter.label},
                                    )

                                    if (sortedFilter.values) {
                                        return (
                                            <ListingFiltersItem
                                                key={sortedFilter.attributeId}
                                                filter={sortedFilter}
                                                selectedFilters={selectedFiltersArray}
                                                title={sortedFilter.label}
                                                ariaLabel={formatMessage(
                                                    {
                                                        id: 'label.aria.filter.refinements',
                                                        defaultMessage: 'Filter by {0}',
                                                    },
                                                    {
                                                        0: sortedFilter.label,
                                                    },
                                                )}
                                                screenReaderCategoryLabelToExpand={screenReaderCategoryLabelToExpand}
                                                screenReaderCategoryLabelExpanded={screenReaderCategoryLabelExpanded}
                                                handleFilterToggle={toggleFilter}
                                            />
                                        )
                                    }
                                })}
                            </Accordion>
                        )}
                    </Stack>
                </DrawerBody>

                <DrawerFooter>
                    <VStack {...styles.footerWrapper}>
                        <Button
                            variant="textLink"
                            size="md"
                            width="md"
                            sx={styles.footerButtons}
                            onClick={() => {
                                handleReset()
                                closeDrawer()
                            }}
                            disabled={resetFiltersDisabled}
                        >
                            <FormattedMessage id="remove.all.active.filter" defaultMessage="Reset filters" />
                        </Button>

                        <Button
                            variant="primary"
                            size="md"
                            width="lg"
                            sx={styles.footerButtons}
                            onClick={() => {
                                onConfirmFilters?.()
                                closeDrawer()
                            }}
                        >
                            <FormattedMessage id="label.plp.filters.showResults" defaultMessage="Show N results" values={{0: filterTotals}} />
                        </Button>
                    </VStack>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    )
})

FiltersList.displayName = 'FiltersList'

export {FiltersList}
